import _ from 'lodash';
import Embed from 'quill/blots/embed';
import type QuillScroll from 'quill/blots/scroll';
import type { ReactElement } from 'react';
import React from 'react';
import { createPortal } from 'react-dom';
import { MOD_KEY } from '../../constants/os';
import { IS_EXTENSION } from '../../constants/platform';
import INTEGRATION_MENTIONS, {
  Behaviors,
} from '../../integrations/IntegrationMentions';
import { LINK_TYPE_ATTRIBUTE, LINK_TYPES } from '../constants';
import ExternalPostMention from '../mentions/ExternalPostMention';
import GroupMention from '../mentions/GroupMention';
import IntegrationMention from '../mentions/IntegrationMention';
import PostMention from '../mentions/PostMention';
import TopicMention from '../mentions/TopicMention';
import UserMention from '../mentions/UserMention';
import getEditorMentionUrl from '../utils/getEditorMentionUrl';
import styles from './Mention.module.scss';
import Scroll from './Scroll';
import MentionBase, { MENTION_CONTAINER_CLASS } from '../mentions/MentionBase';
import type { Mention as MentionType } from '../types';
import Diff from './Diff';
import VersionChange from './VersionChange';
import Comment from './Comment';
import Annotation from './Annotation';
import Reaction from './Reaction';

type MentionCacheUpdater = (value: unknown) => void;

export interface MentionComponentProps {
  id: string;
  link: boolean;
  type: string;
  params?: string;
  blot: Embed;
  showHoverCard: boolean;
  content: string | null;
  mentionText: string | null;
  updateCache: MentionCacheUpdater;
  selected?: boolean;
}

type MentionComponent = (props: MentionComponentProps) => ReactElement;

class Mention extends Embed {
  static blotName = 'mention';
  static className = 'ql-mention';
  static tagName = 'SPAN';

  static shouldLazyLoad = true;

  static mentionComponents: {
    [type: string]: MentionComponent;
  } = {
    post: PostMention,
    user: UserMention,
    topic: TopicMention,
    group: GroupMention,
    'external-post': ExternalPostMention,
    ...Behaviors.Mention.reduce(
      (ret, name) => ({
        ...ret,
        [INTEGRATION_MENTIONS[name].mentionType]: IntegrationMention,
      }),
      {},
    ),
  };

  static create(value: MentionType) {
    const node = super.create(value) as HTMLSpanElement;
    node.setAttribute('data-id', value.id);
    node.setAttribute('data-type', value.type);
    if (value.params) {
      node.setAttribute('data-params', value.params);
    }
    node.classList.add(styles.container);
    return node;
  }

  static formats(domNode: Element) {
    const attributes: {
      content?: string | null;
      'mention-text'?: string | null;
    } = {};

    if (domNode.hasAttribute('data-content')) {
      attributes.content = domNode.getAttribute('data-content');
    }
    if (domNode.hasAttribute('data-mention-text')) {
      attributes['mention-text'] = domNode.getAttribute('data-mention-text');
    }
    return _.isEmpty(attributes) ? null : attributes;
  }

  static value(domNode: Element) {
    return {
      id: domNode.getAttribute('data-id'),
      type: domNode.getAttribute('data-type'),
      ...(domNode.hasAttribute('data-params')
        ? { params: domNode.getAttribute('data-params') }
        : {}),
    };
  }

  id: string;
  updateCache: MentionCacheUpdater;

  constructor(scroll: QuillScroll, domNode: HTMLElement) {
    super(scroll, domNode);
    this.id = _.uniqueId();
    this.updateCache = this.format.bind(this, 'content');
    this.contentNode.classList.add(styles.content);

    const openInNewTab = () => {
      const { id, type, params } = this.statics.value(this.domNode);
      const formats = this.statics.formats(this.domNode) || {};
      const url = getEditorMentionUrl({
        id,
        type,
        content: formats.content,
        params,
      });
      if (url) {
        window.open(url, '_blank', 'noopener');
      }
    };

    domNode.addEventListener('click', (event) => {
      if (!scroll.isEnabled()) return;

      if (event[MOD_KEY] || event.detail === 2) {
        openInNewTab();
      }
    });
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  format(name: string, value: unknown) {
    if (
      (name === 'content' ||
        name === 'mention-text' ||
        name === Diff.attrName ||
        name === VersionChange.attrName) &&
      // @ts-expect-error
      this.domNode.getAttribute(`data-${name}`) !== value
    ) {
      if (value == null) {
        // @ts-expect-error
        this.domNode.removeAttribute(`data-${name}`);
      } else {
        // @ts-expect-error
        this.domNode.setAttribute(`data-${name}`, value);
      }
      if (name === 'content') {
        if (typeof value === 'string') {
          this.contentNode.setAttribute('data-content', value);
        } else {
          this.contentNode.removeAttribute('data-content');
        }
      }
    } else if (
      [Comment.attrName, Reaction.attrName, Annotation.attrName].includes(name)
    ) {
      super.format(name, value);
    }
  }

  formatAt(index: number, length: number, name: string, value: unknown) {
    if (
      [
        Comment.attrName,
        Reaction.attrName,
        Annotation.attrName,
        'content',
        'mention-text',
        Diff.attrName,
        VersionChange.attrName,
      ].includes(name)
    ) {
      super.formatAt(index, length, name, value);
    }
  }

  getReferenceNodeForBounds() {
    return (
      this.contentNode.querySelector(`.${MENTION_CONTAINER_CLASS}`) ??
      this.domNode
    );
  }

  html() {
    const { id, type, params } = this.statics.value(this.domNode);
    const formats = this.statics.formats(this.domNode) || {};
    const url = getEditorMentionUrl({
      id,
      type,
      content: formats.content,
      params,
    });
    return (
      url &&
      `<a ${LINK_TYPE_ATTRIBUTE}="${LINK_TYPES.mention}" href="${url}">${
        formats['mention-text'] || formats.content || url
      }</a>`
    );
  }

  renderPortal({
    readOnly,
    selected,
  }: {
    readOnly: boolean;
    selected: boolean;
  }) {
    const { id, type, params } = this.statics.value(this.domNode);
    const formats = this.statics.formats(this.domNode) || {};
    const MentionComponent = Mention.mentionComponents[type];

    if (!MentionComponent) {
      // legacy mentions like clubhouse-story don't have a defined MentionComponent,
      // so we fall back to keep the basic expected bevahior (like diffs) working
      return createPortal(
        <MentionBase type={type} content={formats.content} truncate />,
        this.contentNode,
      );
    }

    return createPortal(
      <MentionComponent
        id={id}
        link={readOnly}
        type={type}
        blot={this}
        showHoverCard={!IS_EXTENSION}
        content={formats.content}
        mentionText={formats['mention-text']}
        updateCache={this.updateCache}
        selected={selected}
        params={params}
      />,
      this.contentNode,
    );
  }
}

export default Mention;
